import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, MapPin, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { format, parse } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCalendar } from '../../contexts/CalendarContext';
import SelectCalendarModal from '../Calendar/SelectCalendarModal';
import EventParticipantsModal from './EventParticipantsModal';

/**
 * Renders event_share message as a card with Katılıyorum / Katılmıyorum / Belki buttons.
 * Creator is auto-attending and cannot change status.
 * "Katılıyorum" with multiple calendars opens calendar picker; participants list opens on label click.
 */
const EventCardMessage = ({
  message,
  isOwnMessage,
  messageId,
  chatType = 'dm',
  friendId = null,
  groupId = null,
}) => {
  const { t, i18n } = useTranslation('chat');
  const dateLocale = getDateFnsLocale(i18n.language);
  const { user } = useAuth();
  const { sendEventResponse, getEventParticipants, lastEventResponse } = useChat();
  const { calendars = [] } = useCalendar();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myStatus, setMyStatus] = useState(null);
  const [showSelectCalendarModal, setShowSelectCalendarModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [participantsModalStatus, setParticipantsModalStatus] = useState(null);

  const mid = messageId || message?.id;
  const isDm = chatType === 'dm';
  const senderId = message?.sender_id ?? message?.senderId;
  const isCreator = Boolean(senderId && user?.id && String(senderId) === String(user.id));
  const isCancelled = message?.type === 'event_cancelled';

  const fetchParticipants = useCallback(async () => {
    if (!mid) return;
    setLoading(true);
    try {
      const res = await getEventParticipants(mid, isDm ? null : groupId);
      const list = res?.participants || [];
      setParticipants(list);
      const me = list.find((p) => String(p.user_id) === String(user?.id));
      setMyStatus(me?.status || null);
    } catch (e) {
      console.warn('EventCardMessage fetch participants error:', e);
    } finally {
      setLoading(false);
    }
  }, [mid, isDm, groupId, getEventParticipants, user?.id]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  useEffect(() => {
    if (lastEventResponse?.messageId === mid && (isDm ? !lastEventResponse?.groupId : lastEventResponse?.groupId === groupId)) {
      fetchParticipants();
    }
  }, [lastEventResponse?.messageId, lastEventResponse?.groupId, mid, isDm, groupId, fetchParticipants]);

  const doSendResponse = useCallback(
    async (status, calendarId = null) => {
      if (!mid) return;
      setLoading(true);
      try {
        await sendEventResponse(mid, status, isDm ? null : groupId, calendarId);
        setMyStatus(status);
        const res = await getEventParticipants(mid, isDm ? null : groupId);
        setParticipants(res?.participants || []);
      } catch (e) {
        console.warn('Event response error:', e);
        if (e?.response?.data?.error === 'CREATOR_CANNOT_CHANGE') return;
        const isCancelledError = e?.payload?.error === 'EVENT_CANCELLED' || e?.response?.data?.error === 'EVENT_CANCELLED';
        const errMsg = isCancelledError ? t('eventCard.errors.eventCancelled') : (e?.message || t('eventCard.errors.generic'));
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: { type: 'error', title: t('common:errorTitle'), message: errMsg },
          })
        );
      } finally {
        setLoading(false);
      }
    },
    [mid, isDm, groupId, sendEventResponse, getEventParticipants]
  );

  const handleResponse = async (status) => {
    if (isCreator) return;
    if (status === 'attending') {
      const count = calendars?.length ?? 0;
      if (count === 0) {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: { type: 'info', title: t('eventCard.calendarRequired.title'), message: t('eventCard.calendarRequired.message') },
          })
        );
        return;
      }
      if (count === 1) {
        await doSendResponse('attending', calendars[0]?.id);
        return;
      }
      setShowSelectCalendarModal(true);
      return;
    }
    await doSendResponse(status);
  };

  const handleSelectCalendarForAttending = (cal) => {
    setShowSelectCalendarModal(false);
    doSendResponse('attending', cal?.id);
  };

  const title = message?.event_title || message?.eventTitle || t('eventCard.titleFallback');
  const start = message?.event_start || message?.eventStart;
  const end = message?.event_end || message?.eventEnd;
  const allDay = message?.event_all_day === true || message?.event_all_day === 'true';
  const description = message?.event_description || message?.eventDescription || '';
  const location = message?.event_location || message?.eventLocation || '';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = dateStr.includes('T') ? new Date(dateStr) : parse(dateStr, 'yyyy-MM-dd', new Date());
      return format(d, 'd MMM yyyy', { locale: dateLocale });
    } catch {
      return dateStr;
    }
  };

  const attendingCount = participants.filter((p) => p.status === 'attending').length;
  const maybeCount = participants.filter((p) => p.status === 'maybe').length;
  const notCount = participants.filter((p) => p.status === 'not_attending').length;

  const participantLabels = [
    { status: 'attending', label: t('eventCard.rsvp.attending'), count: attendingCount },
    { status: 'maybe', label: t('eventCard.rsvp.maybe'), count: maybeCount },
    { status: 'not_attending', label: t('eventCard.rsvp.notAttending'), count: notCount },
  ];

  const cardBg    = isOwnMessage ? 'bg-[#1d3557] border-[#3d8ef0]/25' : 'bg-[var(--color-bg-muted)] border-white/[0.09]';
  const titleBg   = isOwnMessage ? 'bg-white/[0.07]'                  : 'bg-[var(--color-bg-elevated)]';
  const metaColor = 'text-[var(--color-text-muted)]';
  const divider   = 'border-white/[0.08]';

  const rsvpBtn = (status) => {
    const isActive = myStatus === status;
    if (isActive) return 'bg-[#2563eb] text-white';
    return isOwnMessage
      ? 'bg-white/[0.08] hover:bg-white/[0.15] text-[var(--color-text-secondary)]'
      : 'bg-[#161f31] hover:bg-[#24354f] text-[var(--color-text-secondary)] border border-white/[0.07]';
  };

  if (isCancelled) {
    return (
      <div className={`rounded-2xl border ${cardBg} overflow-hidden min-w-[220px] max-w-[300px] opacity-70`}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]">
          <Calendar className="w-4 h-4 flex-shrink-0 text-[#3d8ef0]" />
          <span className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">{t('eventCard.header')}</span>
          <span className="ml-auto px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-red-500/15 text-red-400 border border-red-500/20">
            {t('eventCard.cancelledBadge')}
          </span>
        </div>
        <div className="px-4 py-3">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {message?.content || message?.message || t('eventCard.cancelledDefaultMessage')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`rounded-2xl border ${cardBg} overflow-hidden min-w-[220px] max-w-[300px]`}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]">
          <Calendar className="w-4 h-4 flex-shrink-0 text-[#3d8ef0]" />
          <span className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">{t('eventCard.header')}</span>
        </div>
        {/* Body */}
        <div className="px-4 py-3 space-y-2">
          <div className={`${titleBg} rounded-xl px-3 py-2.5`}>
            <p className="text-sm font-semibold text-[var(--color-text-primary)] break-words">{title}</p>
          </div>
          {start && (
            <div className={`flex items-center gap-2 text-xs ${metaColor}`}>
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{formatDate(start)}{!allDay && t('eventCard.timedSuffix')}</span>
            </div>
          )}
          {end && end !== start && (
            <div className={`text-xs ${metaColor} ml-5`}>{t('eventCard.endLabel', { date: formatDate(end) })}</div>
          )}
          {location && (
            <div className={`flex items-center gap-2 text-xs ${metaColor}`}>
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="line-clamp-1">{location}</span>
            </div>
          )}
          {description && (
            <p className={`text-xs ${metaColor} line-clamp-2`}>{description}</p>
          )}
        </div>

        {/* RSVP Butonları */}
        <div className={`px-4 pb-3 pt-2 border-t ${divider}`}>
          <div className="flex gap-1.5 mb-2">
            {[
              { status: 'attending',     label: t('eventCard.rsvp.attending'),   Icon: CheckCircle },
              { status: 'not_attending', label: t('eventCard.rsvp.notAttending'),  Icon: XCircle     },
              { status: 'maybe',         label: t('eventCard.rsvp.maybe'),         Icon: HelpCircle  },
            ].map(({ status, label, Icon }) => (
              <button
                key={status}
                type="button"
                disabled={loading || isCreator}
                onClick={() => handleResponse(status)}
                title={isCreator ? t('eventCard.creatorCannotChangeTooltip') : label}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors flex-1 justify-center ${
                  isCreator ? 'opacity-60 cursor-not-allowed' : ''
                } ${rsvpBtn(status)}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
          {participants.length > 0 && (
            <div className={`flex flex-wrap items-center gap-x-1 text-[11px] ${metaColor}`}>
              {participantLabels.map(({ status, label, count }, index) => (
                <React.Fragment key={status}>
                  {index > 0 && <span className="opacity-50">·</span>}
                  <button
                    type="button"
                    onClick={() => { setParticipantsModalStatus(status); setShowParticipantsModal(true); }}
                    className="hover:text-[var(--color-text-secondary)] hover:underline focus:outline-none transition-colors"
                  >
                    {label}: {count}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      <SelectCalendarModal
        isOpen={showSelectCalendarModal}
        onClose={() => setShowSelectCalendarModal(false)}
        calendars={calendars || []}
        onSelect={handleSelectCalendarForAttending}
      />

      <EventParticipantsModal
        isOpen={showParticipantsModal}
        onClose={() => { setShowParticipantsModal(false); setParticipantsModalStatus(null); }}
        participants={participants}
        filterStatus={participantsModalStatus}
        title={
          participantsModalStatus === 'attending'
            ? t('eventCard.rsvp.attending')
            : participantsModalStatus === 'maybe'
            ? t('eventCard.rsvp.maybe')
            : participantsModalStatus === 'not_attending'
            ? t('eventCard.rsvp.notAttending')
            : t('eventCard.rsvp.allParticipants')
        }
      />
    </>
  );
};

export default EventCardMessage;
